// interrupts: an NWS warning, a big nearby quake or a trading halt pre-empts the loop or a feature, with the band
// turned red, for a few seconds per item, then the channel resumes. Rules are `interrupts` in config/clock.json.
// Each is shown once when it first appears; warnings come back every repeatMinutes while they're in effect.
import { registerDisplay, isLocationActive } from '../navigation.mjs';
import STATUS from '../status.mjs';
import { escapeHtml } from '../utils/string.mjs';
import FeatureDisplay from './feature-display.mjs';
import { data } from './snapshot.mjs';
import { cutTo } from './scheduler.mjs';
import tierOf from './advisories.mjs';
import { beginOverride, endOverride } from './controls.mjs';
import { interruptIntro, interruptClose } from './cadence.mjs';
import { isPlaying as bumperOn } from './bumper.mjs';
import { time, weekday, isToday } from './format.mjs';

const { clock, water } = window.RHL;
const rules = clock.interrupts;
const major = rules.major ?? null;
const MINUTE = 60000;
// id → when it was last on air, when it was first, and how many times
const shown = new Map();

const when = (t, now) => `${isToday(t, now) ? '' : `${weekday(t)} `}${time(t)}`;
const miles = (lat, lon) => {
	const rad = Math.PI / 180;
	const dLat = (lat - water.point.lat) * rad;
	const dLon = (lon - water.point.lon) * rad;
	const a = Math.sin(dLat / 2) ** 2 + Math.cos(lat * rad) * Math.cos(water.point.lat * rad) * Math.sin(dLon / 2) ** 2;
	return 3959 * 2 * Math.asin(Math.sqrt(a));
};
const nyDate = (now) => new Intl.DateTimeFormat('en-US', {
	timeZone: 'America/New_York', month: '2-digit', day: '2-digit', year: 'numeric',
}).format(now);
// a halt's time is New York wall-clock time; how many minutes ago it was
const haltAge = (halt, now) => {
	const [h, m] = halt.time.split(':').map(Number);
	const [nh, nm] = new Intl.DateTimeFormat('en-GB', { timeZone: 'America/New_York', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' })
		.format(now).split(':').map(Number);
	return nh * 60 + nm - (h * 60 + m);
};

// the major tier (Oct 26): an Extreme NWS alert or one of the listed events, a big close quake, a level 3 halt
const majorWeather = (a) => !!major && (a.severity === 'Extreme' || major.nwsEvents.includes(a.event));
const majorQuake = (q) => !!major && major.quake.some((r) => q.mag >= r.minMagnitude && miles(q.lat, q.lon) * 1.609 <= r.withinKm);
const majorHalt = (h) => !!major && major.halts.includes(h.reason);

// one on-air item per alert, quake or halt
const weatherItem = (a, now) => ({
	id: a.id,
	kind: 'weather',
	major: majorWeather(a),
	instruction: a.instruction,
	repeat: rules.nws.repeatMinutes,
	title: a.event.includes('Warning') ? ['Weather', 'Warning'] : ['Weather', 'Alert'],
	html: `<div class="alert-head">${escapeHtml(a.event)}</div>
		<div class="alert-when">${a.ends ? `Until ${when(a.ends, now)}` : 'In effect'}</div>
		<div class="alert-text">${escapeHtml(a.summary || a.headline || '')}</div>`,
});
const quakeItem = (q) => ({
	id: q.id,
	kind: 'quake',
	major: majorQuake(q),
	instruction: majorQuake(q) ? 'Drop, cover and hold on through any aftershock. Check for gas leaks and damage before moving around.' : null,
	title: ['Earthquake', 'Report'],
	html: `<div class="alert-head">Magnitude ${q.mag.toFixed(1)}</div>
		<div class="alert-when">${escapeHtml(q.place)}</div>
		<div class="alert-text">At ${time(q.t)}, about ${Math.round(miles(q.lat, q.lon))} miles from Russian Hill. Preliminary, from the USGS.</div>`,
});
const haltItem = (h) => ({
	id: `${h.symbol}-${h.date}-${h.time}`,
	kind: 'halt',
	major: majorHalt(h),
	instruction: majorHalt(h) ? 'A level 3 breaker closes US stock markets for the rest of the day.' : null,
	title: ['Trading', 'Halt'],
	html: /^MWC/.test(h.reason)
		? `<div class="alert-head">Market-wide circuit breaker</div>
			<div class="alert-when">${escapeHtml(h.reason)} &middot; ${escapeHtml(h.time)} ET</div>
			<div class="alert-text">Trading is halted across US stock markets.</div>`
		: `<div class="alert-head">${escapeHtml(h.symbol)} halted</div>
			<div class="alert-when">${escapeHtml(h.time)} ET &middot; reason ${escapeHtml(h.reason)}</div>
			<div class="alert-text">${escapeHtml(h.name)}${h.resumed ? `. Resumed ${escapeHtml(h.resumed)} ET.` : '.'}</div>`,
});

// everything that would interrupt right now: warnings, quakes, halts
const active = (now = new Date()) => {
	const today = nyDate(now);
	return [
		...(data('alerts') ?? [])
			.filter((a) => rules.nws.events.some((word) => a.event.includes(word)) && tierOf(a.event) !== 'ignore' && (!a.ends || new Date(a.ends) > now))
			.map((a) => weatherItem(a, now)),
		...(data('quakes') ?? [])
			.filter((q) => q.mag >= rules.quake.minMagnitude && now - new Date(q.t) < rules.quake.withinMinutes * MINUTE)
			.map(quakeItem),
		...(data('halts') ?? [])
			.filter((h) => h.date === today && haltAge(h, now) >= 0 && haltAge(h, now) < rules.halts.withinMinutes)
			.map(haltItem),
	];
};

// test items for the control room, built the same way as real ones and marked TEST in their text
const testItem = (kind, now = new Date()) => {
	const id = `test-${kind}-${now.getTime()}`;
	if (kind === 'weather') {
		return {
			...weatherItem({
				id, event: 'TEST Wind Warning', ends: new Date(now.getTime() + 3 * 3600000).toISOString(), summary: 'TEST. This is a test of the interrupt screen from the control room. No warning is in effect.',
			}, now),
			repeat: null,
		};
	}
	if (kind === 'quake') {
		return quakeItem({
			id, mag: 4.6, place: 'TEST, 8 km NE of Berkeley, CA', t: new Date(now.getTime() - 120000).toISOString(), lat: 37.9, lon: -122.22,
		});
	}
	if (kind === 'quake-major') {
		return quakeItem({
			id, mag: 5.4, place: 'TEST, 5 km SE of Daly City, CA', t: new Date(now.getTime() - 120000).toISOString(), lat: 37.66, lon: -122.44,
		});
	}
	const hhmm = new Intl.DateTimeFormat('en-GB', { timeZone: 'America/New_York', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }).format(now);
	return haltItem({
		symbol: 'TEST', name: 'TEST halt from the control room', date: nyDate(now), time: hhmm, reason: 'LUDP', resumed: null,
	});
};

// the ones due on air: never shown, a warning whose repeat interval has passed, or a major one on its tighter
// schedule (every 15 minutes for the first hour, then every 30; a major quake three times in its first hour)
const due = (now = new Date()) => active(now).filter((item) => {
	const seen = shown.get(item.id);
	if (!seen) return true;
	if (item.major) {
		if (item.kind === 'quake') return seen.count < major.quakeRepeats && now - seen.last >= 20 * MINUTE;
		const every = now - seen.first < 60 * MINUTE ? major.repeatMinutes.firstHour : major.repeatMinutes.after;
		return now - seen.last >= every * MINUTE;
	}
	return item.repeat && now - seen.last >= item.repeat * MINUTE;
});
const markShown = (item, now) => {
	const seen = shown.get(item.id);
	shown.set(item.id, { first: seen?.first ?? now, last: now, count: (seen?.count ?? 0) + 1 });
};

class Interrupt extends FeatureDisplay {
	constructor(navId, elemId) {
		super(navId, elemId, 'Interrupt');
	}

	// eslint-disable-next-line class-methods-use-this
	hasData() {
		return active().length > 0;
	}

	// always ready: with nothing active it simply has no screens
	// eslint-disable-next-line class-methods-use-this
	isReady() {
		return true;
	}

	// scheduled: the items due; from the display list: everything active now, for a look
	// eslint-disable-next-line class-methods-use-this
	buildPages(now, options) {
		const items = options.items ?? active(now);
		return items.flatMap((item) => {
			const first = {
				major: item.major,
				seconds: item.major ? Math.round(major.seconds / 2) : rules.seconds,
				title: item.title,
				html: `<div class="interrupt-body">${item.html}</div>`,
			};
			if (!item.major) return [first];
			return [first, {
				major: true,
				seconds: major.seconds - first.seconds,
				title: item.title,
				html: `<div class="interrupt-body"><div class="alert-head">What to do</div><div class="alert-text">${escapeHtml(item.instruction || 'Follow official instructions.')}</div></div>`,
			}];
		});
	}

	showCanvas(navCmd) {
		document.querySelector('#divTwc')?.classList.add('rhl-interrupting');
		document.querySelector('#divTwc')?.classList.toggle('rhl-major', this.pages.some((p) => p.major));
		super.showCanvas(navCmd);
	}

	hideCanvas() {
		document.querySelector('#divTwc')?.classList.remove('rhl-interrupting', 'rhl-major');
		super.hideCanvas();
	}
}

const display = new Interrupt(25, 'interrupt');
registerDisplay(display);

// a held or paused page plays for the interrupt, then goes back to how it was (decided Oct 26)
const airInterrupt = (items) => {
	beginOverride();
	const intro = interruptIntro(items[0].kind);
	// a major one opens on the foghorn whatever it is
	if (items.some((item) => item.major)) intro.sting = 'foghorn';
	const aired = cutTo(display, { items }, { intro, outro: interruptClose });
	if (!aired) {
		endOverride();
		return false;
	}
	// when its pages run out, the held or paused page gets its state back
	const finish = display.onFinish;
	display.onFinish = (shown) => {
		display.onFinish = finish;
		endOverride();
		finish?.(shown);
	};
	return true;
};

// check every five seconds; an interrupt waits only for another interrupt already on air. It airs whether or not the
// page is playing: a skip or a pause never keeps a warning off the screen
setInterval(() => {
	if (!isLocationActive() || display.status !== STATUS.loaded) return;
	// one on air already, or a bumper mid-play: try again on the next check
	if (display.timing.totalScreens > 0 || bumperOn()) return;
	const now = new Date();
	const items = due(now);
	if (!items.length) return;
	if (airInterrupt(items)) items.forEach((item) => markShown(item, now.getTime()));
}, 5000);

// the control room: a test interrupt of one kind, now
const forceInterrupt = (kind) => {
	if (display.timing.totalScreens > 0 || bumperOn()) return false;
	return airInterrupt([testItem(kind)]);
};

export default forceInterrupt;
